// src/services/worker/assignWorkerFacility.js

import {
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";

import { db } from "../../firebase";
import { hasApprovedWorkerReadiness } from "../../core/workforce/workerReadiness";

import {
  normalizeId,
  normalizePlatformRole,
} from "../../security/permissions";

import {
  finalizeFacilityMembership,
} from "../../finalizeFacilityMembership";


/*
 * =========================================================
 * WORKER OPERATIONAL APPROVAL
 * =========================================================
 *
 * Facility assignment may happen before or after worker
 * approval.
 *
 * Do not create accessProfiles/{uid} merely because a
 * facility was selected.
 *
 * Operational access is finalized only when the worker has
 * completed approval/compliance AND has facility scope.
 */

function isWorkerOperationallyApproved(worker = {}) {
  return hasApprovedWorkerReadiness(worker);
}


/*
 * =========================================================
 * FACILITY ASSIGNMENT
 * =========================================================
 */

export async function assignWorkerToFacility(
  workerUid,
  facilityId
) {
  const cleanWorkerUid =
    String(
      workerUid || ""
    ).trim();

  const cleanFacilityId =
    normalizeId(
      facilityId
    );

  if (!cleanWorkerUid) {
    throw new Error(
      "Missing worker UID."
    );
  }

  if (!cleanFacilityId) {
    throw new Error(
      "Select a facility."
    );
  }


  /* =====================================================
     LOAD WORKER
     ===================================================== */

  const workerRef =
    doc(
      db,
      "users",
      cleanWorkerUid
    );

  const workerSnap =
    await getDoc(
      workerRef
    );

  if (!workerSnap.exists()) {
    throw new Error(
      "Worker record not found."
    );
  }

  const workerData =
    workerSnap.data() || {};

  const workerRole =
    normalizePlatformRole(
      workerData?.platformRole ||
        workerData?.role ||
        ""
    );

  if (workerRole !== "worker") {
    throw new Error(
      "Only worker accounts can be assigned to facilities."
    );
  }


  /* =====================================================
     LOAD TARGET FACILITY
     ===================================================== */

  const facilityRef =
    doc(
      db,
      "facilityProfiles",
      cleanFacilityId
    );

  const facilitySnap =
    await getDoc(
      facilityRef
    );

  if (!facilitySnap.exists()) {
    throw new Error(
      `Facility profile not found: ${cleanFacilityId}`
    );
  }

  const facilityData =
    facilitySnap.data() || {};

  const cleanTenantId =
    normalizeId(
      facilityData?.tenantId ||
        facilityData?.tenantKey ||
        ""
    );

  if (!cleanTenantId) {
    throw new Error(
      `Facility ${cleanFacilityId} is missing tenantId.`
    );
  }


  /* =====================================================
     PROTECT EXISTING WORKER TENANT
     ===================================================== */

  const existingTenantId =
    normalizeId(
      workerData?.tenantId ||
        workerData?.tenantKey ||
        ""
    );

  /*
   * Facility assignment may establish a missing worker
   * tenant, but it must never silently move an existing
   * worker from one tenant into another.
   */
  if (
    existingTenantId &&
    existingTenantId !==
      cleanTenantId
  ) {
    throw new Error(
      `Worker belongs to tenant "${existingTenantId}" and cannot be assigned to facility "${cleanFacilityId}" in tenant "${cleanTenantId}".`
    );
  }


  /* =====================================================
     BUILD CANONICAL FACILITY MEMBERSHIP
     ===================================================== */

  const existingFacilityIds =
    [
      workerData?.facilityId,
      workerData?.primaryFacilityId,

      ...(Array.isArray(
        workerData?.facilityIds
      )
        ? workerData.facilityIds
        : []),

      ...(Array.isArray(
        workerData?.allowedFacilityIds
      )
        ? workerData.allowedFacilityIds
        : []),

      ...(Array.isArray(
        workerData?.assignedFacilityIds
      )
        ? workerData.assignedFacilityIds
        : []),
    ]
      .map(normalizeId)
      .filter(Boolean)
      .filter(
        (id) =>
          id !== "all"
      );

  const nextFacilityIds =
    Array.from(
      new Set([
        ...existingFacilityIds,
        cleanFacilityId,
      ])
    );


  /* =====================================================
     VALIDATE EXISTING FACILITIES AGAINST TENANT
     ===================================================== */

  /*
   * Do this before mutating users/{uid}.
   *
   * If a legacy worker already contains a facility from
   * another tenant, surface the bad membership instead of
   * preserving it into the canonical record.
   */
  await Promise.all(
    nextFacilityIds.map(
      async (candidateFacilityId) => {
        if (
          candidateFacilityId ===
          cleanFacilityId
        ) {
          return;
        }

        const candidateSnap =
          await getDoc(
            doc(
              db,
              "facilityProfiles",
              candidateFacilityId
            )
          );

        if (!candidateSnap.exists()) {
          throw new Error(
            `Existing worker facility profile not found: ${candidateFacilityId}`
          );
        }

        const candidateData =
          candidateSnap.data() || {};

        const candidateTenantId =
          normalizeId(
            candidateData?.tenantId ||
              candidateData?.tenantKey ||
              ""
          );

        if (!candidateTenantId) {
          throw new Error(
            `Existing worker facility ${candidateFacilityId} is missing tenantId.`
          );
        }

        if (
          candidateTenantId !==
          cleanTenantId
        ) {
          throw new Error(
            `Worker facility "${candidateFacilityId}" belongs to tenant "${candidateTenantId}", not "${cleanTenantId}".`
          );
        }
      }
    )
  );


  /* =====================================================
     WRITE CANONICAL WORKER SCOPE
     ===================================================== */

  await updateDoc(
    workerRef,
    {
      tenantId:
        cleanTenantId,

      facilityId:
        cleanFacilityId,

      primaryFacilityId:
        cleanFacilityId,

      facilityIds:
        nextFacilityIds,

      allowedFacilityIds:
        nextFacilityIds,

      /*
       * Keep this legacy membership field synchronized
       * while older Planner/Workforce queries still use it.
       */
      assignedFacilityIds:
        nextFacilityIds,

      facilityAssignedAt:
        serverTimestamp(),

      updatedAt:
        serverTimestamp(),
    }
  );


  /* =====================================================
     FINALIZE OPERATIONAL ACCESS WHEN READY
     ===================================================== */

  const operationallyApproved =
    isWorkerOperationallyApproved(
      workerData
    );

  let accessFinalized =
    false;

  if (operationallyApproved) {
    await finalizeFacilityMembership({
      uid:
        cleanWorkerUid,

      email:
        workerData?.email ||
        workerData?.emailLower ||
        "",

      role:
        "worker",

      platformRole:
        "worker",

      tenantId:
        cleanTenantId,

      status:
        "active",

      facilityId:
        cleanFacilityId,

      primaryFacilityId:
        cleanFacilityId,

      facilityIds:
        nextFacilityIds,

      allowedFacilityIds:
        nextFacilityIds,

      credentials:
        Array.isArray(
          workerData?.credentials
        )
          ? workerData.credentials
          : [],

      disabled:
        false,

      source:
        "worker_facility_assignment",
    });

    accessFinalized =
      true;
  }


  return {
    workerUid:
      cleanWorkerUid,

    tenantId:
      cleanTenantId,

    facilityId:
      cleanFacilityId,

    facilityIds:
      nextFacilityIds,

    operationallyApproved,

    accessFinalized,
  };
}